import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { EinweisungGruppe, EinweisungTeilnehmer, SigeEinweisung } from '../../components/sige-plan/types';
import { MAX_SEITEN } from '../sigeEinweisung';
import {
    A4_GEOMETRY,
    CompanyInfo,
    createLetterhead,
    createSpaceGuard,
    drawFooters,
    drawPage1Header,
    fitLogoBox,
    FOOTER_RESERVE,
    imgFmt,
} from './letterhead';
import { COLORS, formatDate } from './pdfUtils';

/**
 * Die Einweisung in den SiGe-Plan als Geschaeftsbrief.
 *
 * Das Blatt hat zwei Teile, und die trennt bewusst ein Seitenumbruch:
 *   1. INHALT — was eingewiesen wurde, als Stichpunkte. Hoechstens zwei Seiten;
 *      wer den Plan vorliest, haelt keine Einweisung.
 *   2. UNTERSCHRIFTEN — ab neuer Seite, damit das Blatt, das herumgereicht und
 *      unterschrieben wird, den Inhalt nicht zerschneidet.
 *
 * ─── Briefkopf ────────────────────────────────────────────────────────────
 * Ausschliesslich ueber `letterhead.ts` (createLetterhead / drawPage1Header /
 * drawFooters) — dieselbe Implementierung, die Rechnung und Angebot benutzen.
 * Damit traegt die Einweisung garantiert dasselbe Briefpapier; ein eigener
 * Briefkopf waere die dritte Kopie, die auseinanderdriftet.
 *
 * TRAGEND: ist ein Briefpapier hinterlegt, muss JEDE Seite gestempelt werden.
 * Umgeblaettert wird deshalb nur ueber `lh.addPage()`, und die
 * Unterschriftentabelle bekommt `willDrawPage` mit — autoTable legt seine
 * Folgeseiten selbst an, und eine ungestempelte Seite faellt erst beim Kunden
 * auf.
 *
 * ─── Seitenzahl ───────────────────────────────────────────────────────────
 * Erst am Ende gestempelt (drawFooters), wenn die Gesamtzahl feststeht. Wie
 * viele Seiten es geworden sind, sagt `zaehleEinweisungSeiten` verbindlich —
 * `schaetzeSeiten` in services/sigeEinweisung.ts ist nur die Live-Anzeige
 * neben dem Editor und misst keine Textbreiten.
 */

// ─── Typografie ─────────────────────────────────────────────────────────────

const SCHRIFT = {
    titel: 15,
    untertitel: 11,
    feld: 9.5,
    gruppe: 10.5,
    /** Stichpunkte — die Vorgabe des Blattes. */
    punkt: 9.5,
    klein: 8,
};

const ZEILE = {
    feld: 4.6,
    punkt: 4.2,
};

/** Breite der Beschriftungsspalte im Kopfblock in mm. */
const LABEL_BREITE = 34;

/** Einzug des Stichpunkttextes hinter dem Aufzaehlungszeichen in mm. */
const BULLET_EINZUG = 4;

/** Zeilenhoehe einer Unterschriftszeile in mm — Platz fuer einen Namenszug. */
const UNTERSCHRIFT_HOEHE = 14;

/**
 * Blanko-Zeilen, wenn weder Teilnehmer noch Leerzeilen gepflegt sind.
 *
 * Eine Unterschriftenliste ohne eine einzige Zeile ist auf der Baustelle
 * wertlos: genau dann wird das Blatt ausgedruckt und von Hand ausgefuellt.
 */
const BLANKO_MINDESTENS = 5;

const strich = (v?: string | null): string => (v ?? '').trim() || '—';

/** Mehrzeilige Adresse zu einer Zeile — der Kopfblock ist eine Feldliste. */
const einzeilig = (v: string): string => v.replace(/\s*\n\s*/g, ', ').replace(/\s+/g, ' ').trim();

/** „Max Muster, Polier" — die Funktion hinter dem Namen, wenn gepflegt (Plan §1.5). */
const nameMitFunktion = (t: Pick<EinweisungTeilnehmer, 'name' | 'funktion'>): string => {
    const name = (t.name ?? '').trim();
    const funktion = (t.funktion ?? '').trim();
    return funktion ? `${name}, ${funktion}` : name;
};

/** ISO-Zeitstempel als „12.09.2026 07:41" (Ortszeit); Unlesbares wird leer. */
const formatZeitstempel = (iso: string | null | undefined): string => {
    const zeit = Date.parse(iso || '');
    if (Number.isNaN(zeit)) return '';
    const d = new Date(zeit);
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

export interface EinweisungPdfOptionen {
    /** Projektname aus dem App-Projekt (die Einweisung selbst kennt nur die ID). */
    projektName?: string;
    /** Projektnummer — Pflichtangabe auf dem Blatt, siehe Kopfblock. */
    projektNummer?: string;
    /** Anschrift der Baustelle. */
    bauvorhabenAdresse?: string;
    /**
     * Eigenes jsPDF-Objekt statt eines frisch erzeugten.
     *
     * Nur fuer Tests: nur so laesst sich pruefen, dass das Briefpapier auf
     * JEDER Seite genau einmal gestempelt wird. Dasselbe Muster wie
     * `__docFactory` im Rechnungsgenerator.
     */
    __docFactory?: () => jsPDF;
}

export interface EinweisungSeiten {
    /** Seiten des Blattes insgesamt, Unterschriftenliste eingerechnet. */
    seitenGesamt: number;
    /** Seiten VOR der Unterschriftenliste — die Zahl, die auf 2 begrenzt ist. */
    seitenInhalt: number;
    /** Haelt der Inhalt die angestrebten MAX_SEITEN Seiten ein? */
    passt: boolean;
}

// ─── Der Satz ───────────────────────────────────────────────────────────────

const renderEinweisung = (
    doc: jsPDF,
    einweisung: SigeEinweisung,
    company: CompanyInfo,
    optionen?: EinweisungPdfOptionen,
): EinweisungSeiten => {
    const geo = A4_GEOMETRY;
    const { margin, contentWidth, rightX } = geo;

    // Der Beleg steht in EINEM Schwarz wie Rechnung und Angebot; die Aliase
    // bleiben, damit die Zeichenstellen lesbar bleiben.
    const [tp0, tp1, tp2] = COLORS.text.primary;
    const [ln0, ln1, ln2] = COLORS.section.line;

    const lh = createLetterhead(doc, company, geo);
    lh.stampCurrentBg();
    const { addressFieldY, rightColBottom } = drawPage1Header(doc, company, lh, geo);

    // Kein Anschriftfeld: die Einweisung ist kein Brief an einen Empfaenger.
    // Der Inhalt beginnt deshalb dort, wo sonst das Anschriftfeld anfaengt —
    // mit Briefpapier ist das der gepflegte obere Rand, ohne Briefpapier die
    // Unterkante des gezeichneten Briefkopfs.
    let y = lh.hasStationery ? addressFieldY : Math.max(addressFieldY, rightColBottom + 8);

    const ensureSpace = createSpaceGuard(
        lh,
        geo,
        () => y,
        (v) => {
            y = v;
        },
    );

    /**
     * Platz fuer einen Block der Hoehe `hoehe` — oder eine neue Seite.
     *
     * `createSpaceGuard` misst `needed` gegen die BLATTKANTE und klemmt es nur
     * nach unten gegen den Fusszeilenstreifen ab. Ein mehrzeiliger Block muss
     * seine eigene Hoehe deshalb DRAUFRECHNEN: sonst beginnt er zwar noch auf
     * der Seite, aber seine letzten Zeilen landen in der Fusszeile.
     */
    const fussReserve = lh.hasStationery ? lh.botMargin : FOOTER_RESERVE;
    const platzFuer = (hoehe: number) => ensureSpace(hoehe + fussReserve);

    // ── Titel ────────────────────────────────────────────────────────────
    doc.setTextColor(tp0, tp1, tp2);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(SCHRIFT.titel);
    doc.text('Einweisung in den SiGe-Plan', margin, y);
    y += 7;

    const titel = einweisung.titel?.trim();
    if (titel) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.untertitel);
        for (const zeile of doc.splitTextToSize(titel, contentWidth) as string[]) {
            doc.text(zeile, margin, y);
            y += 5;
        }
    }
    y += 3;

    // ── Kopfblock: die Feldliste ─────────────────────────────────────────
    // Projekt-Nr. und Durchfuehrender stehen IMMER, notfalls mit „—". Ein
    // Feld, das mal da ist und mal fehlt, sucht der Leser beim naechsten
    // Blatt an einer Stelle, an der es diesmal nicht steht — und beides ist
    // fachlich gefordert: ohne sie ist nicht belegbar, wer wen worin
    // eingewiesen hat.
    const felder: Array<[string, string]> = [];

    const nummer = Number(einweisung.nummer);
    felder.push(['Einweisung Nr.', Number.isFinite(nummer) && nummer > 0 ? String(nummer) : '—']);

    const datum = formatDate(einweisung.datum) || '—';
    const uhrzeit = einweisung.uhrzeit?.trim();
    felder.push(['Datum', uhrzeit ? `${datum}, ${uhrzeit} Uhr` : datum]);

    const ort = einweisung.ort?.trim();
    if (ort) felder.push(['Ort', einzeilig(ort)]);

    felder.push(['Projekt-Nr.', strich(optionen?.projektNummer)]);

    const projektName = optionen?.projektName?.trim();
    if (projektName) felder.push(['Projekt', einzeilig(projektName)]);

    const bauvorhaben = optionen?.bauvorhabenAdresse?.trim();
    if (bauvorhaben) felder.push(['Bauvorhaben', einzeilig(bauvorhaben)]);

    const planTeile: string[] = [];
    if (einweisung.plan_nr?.trim()) planTeile.push(`Nr. ${einweisung.plan_nr.trim()}`);
    if (einweisung.plan_stand?.trim()) planTeile.push(`Stand ${einweisung.plan_stand.trim()}`);
    if (planTeile.length > 0) felder.push(['SiGe-Plan', planTeile.join(', ')]);

    felder.push(['Durchführender', strich(einweisung.durchfuehrender_name)]);

    const dauer = Number(einweisung.dauer_min);
    if (Number.isFinite(dauer) && dauer > 0) felder.push(['Dauer', `${dauer} Minuten`]);

    doc.setFontSize(SCHRIFT.feld);
    for (const [label, wert] of felder) {
        const wertZeilen = doc.splitTextToSize(wert, contentWidth - LABEL_BREITE) as string[];
        platzFuer(wertZeilen.length * ZEILE.feld);
        doc.setFont('helvetica', 'bold');
        doc.text(label, margin, y);
        doc.setFont('helvetica', 'normal');
        wertZeilen.forEach((zeile, i) => {
            doc.text(zeile, margin + LABEL_BREITE, y + i * ZEILE.feld);
        });
        y += Math.max(1, wertZeilen.length) * ZEILE.feld;
    }

    y += 2;
    doc.setDrawColor(ln0, ln1, ln2);
    doc.setLineWidth(0.2);
    doc.line(margin, y, rightX, y);
    y += 6;

    // ── Papierform: der Vermerk statt der Liste ──────────────────────────
    // Der Datensatz ist vollwertig, nur unterschrieben wurde auf Papier. Das
    // muss auf dem Blatt stehen, sonst sieht die fehlende Unterschriftenliste
    // wie ein Fehler aus — und der Leser sucht die Unterschriften vergebens,
    // statt die hinterlegte Datei zu oeffnen.
    const istPapier = einweisung.quelle === 'papier';
    if (istPapier) {
        const dateiname = einweisung.dokument_name?.trim();
        const text = dateiname
            ? `Auf Papier durchgeführt. Die unterschriebene Fassung ist als Datei hinterlegt: ${dateiname}`
            : 'Auf Papier durchgeführt. Die unterschriebene Fassung ist als Datei hinterlegt.';
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.feld);
        const zeilen = doc.splitTextToSize(text, contentWidth - 8) as string[];
        const hoehe = zeilen.length * ZEILE.feld + 5;
        platzFuer(hoehe + 4);
        const [by0, by1, by2] = COLORS.status.yellow.bg;
        const [bo0, bo1, bo2] = COLORS.status.yellow.border;
        doc.setFillColor(by0, by1, by2);
        doc.setDrawColor(bo0, bo1, bo2);
        doc.setLineWidth(0.3);
        doc.rect(margin, y, contentWidth, hoehe, 'FD');
        doc.setTextColor(tp0, tp1, tp2);
        zeilen.forEach((zeile, i) => {
            doc.text(zeile, margin + 4, y + 5.5 + i * ZEILE.feld);
        });
        y += hoehe + 7;
    }

    // ── Inhalt: Gruppen mit Stichpunkten ─────────────────────────────────
    // Reihenfolge und Auswahl kommen aus dem Datensatz, nicht aus einer
    // eigenen Sortierung: der Bearbeiter hat sie im Editor festgelegt, und
    // eine Umsortierung beim Drucken waere ein Blatt, das anders aussieht als
    // die Maske, in der es freigegeben wurde. Leere Gruppen fallen weg — eine
    // Ueberschrift ohne Punkte sieht auf dem unterschriebenen Blatt wie ein
    // Druckfehler aus.
    const gruppen: EinweisungGruppe[] = (einweisung.punkte ?? []).filter(
        (g) => (g?.punkte ?? []).some((p) => (p ?? '').trim().length > 0),
    );

    for (const gruppe of gruppen) {
        const punkte = (gruppe.punkte ?? []).map((p) => (p ?? '').trim()).filter(Boolean);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.gruppe);
        doc.setTextColor(tp0, tp1, tp2);
        const titelZeilen = doc.splitTextToSize(gruppe.titel?.trim() || '', contentWidth) as string[];
        // Ueberschrift plus mindestens ein Punkt muessen zusammen passen,
        // sonst steht die Ueberschrift allein am Seitenfuss.
        platzFuer(titelZeilen.length * 5 + ZEILE.punkt * 2);
        for (const zeile of titelZeilen) {
            doc.text(zeile, margin, y);
            y += 5;
        }
        y += 0.5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.punkt);
        for (const punkt of punkte) {
            // Umbrechen, nie abschneiden: ein halber Merksatz auf einem
            // unterschriebenen Blatt behauptet etwas anderes als der ganze.
            const zeilen = doc.splitTextToSize(punkt, contentWidth - BULLET_EINZUG) as string[];
            platzFuer(zeilen.length * ZEILE.punkt);
            doc.text('•', margin, y);
            zeilen.forEach((zeile, i) => {
                doc.text(zeile, margin + BULLET_EINZUG, y + i * ZEILE.punkt);
            });
            y += zeilen.length * ZEILE.punkt + 1.2;
        }
        y += 3;
    }

    // ── Bemerkung ────────────────────────────────────────────────────────
    const bemerkung = einweisung.bemerkung?.trim();
    if (bemerkung) {
        const zeilen = doc.splitTextToSize(bemerkung, contentWidth) as string[];
        platzFuer(zeilen.length * ZEILE.punkt + 8);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.gruppe);
        doc.text('Bemerkung', margin, y);
        y += 5.5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.punkt);
        for (const zeile of zeilen) {
            doc.text(zeile, margin, y);
            y += ZEILE.punkt;
        }
        y += 3;
    }

    const seitenInhalt = doc.getNumberOfPages();

    // ── Unterschriftenliste ──────────────────────────────────────────────
    if (!istPapier) {
        const teilnehmer = (einweisung.teilnehmer ?? []).filter((t) => !!t);
        const gewuenschteLeerzeilen = Math.max(0, Math.floor(Number(einweisung.leerzeilen) || 0));
        const leerzeilen =
            teilnehmer.length === 0 && gewuenschteLeerzeilen === 0
                ? BLANKO_MINDESTENS
                : gewuenschteLeerzeilen;

        // Immer eine frische Seite: das Blatt, das herumgereicht wird, soll
        // nicht auf halber Hoehe unter dem letzten Stichpunkt beginnen.
        lh.addPage();
        y = lh.contentTopFollowing;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(SCHRIFT.untertitel);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text('Teilnehmer und Unterschriften', margin, y);
        y += 4;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.klein);
        doc.text(
            'Mit meiner Unterschrift bestätige ich, in den SiGe-Plan eingewiesen worden zu sein.',
            margin,
            y + 3.5,
        );
        y += 9;

        /** Unterschriftsbild je Tabellenzeile — Index = Zeilenindex der Tabelle. */
        const bilder: Array<string | undefined> = [];
        /** Zeilen, die ueber den Link/QR eingetragen wurden — bekommen das kleine „online". */
        const online: boolean[] = [];
        const body: string[][] = [];
        teilnehmer.forEach((t, i) => {
            // Der Aufrufer reicht die VEREINIGTE Liste herein (Geraet + online,
            // vereinigeTeilnehmer); der Generator kennt keine Online-Tabelle.
            // Online-Zeilen tragen den Zeitstempel in der Spalte Datum — sie
            // sind Nachzuegler, die nicht am Tag des Blattes unterschrieben
            // haben. Geraete-Zeilen lassen die Spalte leer: ihr Datum ist das
            // des Blattes im Kopf, und Blanko-Zeilen brauchen sie zum
            // Ausfuellen von Hand.
            const istOnline = t.quelle === 'online';
            body.push([
                String(i + 1),
                nameMitFunktion(t),
                (t.firma ?? '').trim(),
                istOnline ? formatZeitstempel(t.unterschriebenAm) : '',
                '',
            ]);
            bilder.push(t.unterschrift?.trim() || undefined);
            online.push(istOnline);
        });
        for (let i = 0; i < leerzeilen; i++) {
            body.push([String(teilnehmer.length + i + 1), '', '', '', '']);
            bilder.push(undefined);
            online.push(false);
        }

        autoTable(doc, {
            startY: y,
            head: [['Nr.', 'Name', 'Firma', 'Datum', 'Unterschrift']],
            body,
            theme: 'grid',
            // Die Liste laeuft ueber mehrere Seiten, sobald genug Leute da
            // sind — ohne wiederholte Kopfzeile weiss auf Seite 2 niemand
            // mehr, in welche Spalte er unterschreibt.
            showHead: 'everyPage',
            margin: lh.autoTableMargin(),
            // autoTable legt seine Folgeseiten selbst an — ohne diesen
            // Callback bliebe jede davon ohne Briefpapier.
            willDrawPage: lh.willDrawPage,
            headStyles: {
                fillColor: [255, 255, 255],
                textColor: [tp0, tp1, tp2],
                fontSize: SCHRIFT.klein,
                fontStyle: 'bold',
                lineColor: [ln0, ln1, ln2],
                lineWidth: 0.2,
            },
            styles: {
                fontSize: SCHRIFT.punkt,
                textColor: [tp0, tp1, tp2],
                lineColor: [ln0, ln1, ln2],
                lineWidth: 0.2,
                cellPadding: { top: 2, bottom: 2, left: 2, right: 2 },
                minCellHeight: UNTERSCHRIFT_HOEHE,
                valign: 'middle',
                // Nichts wird abgeschnitten: ein langer Firmenname bricht um
                // und die Zeile wird hoeher.
                overflow: 'linebreak',
            },
            columnStyles: {
                0: { cellWidth: 12, halign: 'center' },
                1: { cellWidth: 'auto' },
                2: { cellWidth: 40 },
                3: { cellWidth: 26, fontSize: SCHRIFT.klein, valign: 'top' },
                4: { cellWidth: 50 },
            },
            didDrawCell: (data) => {
                if (data.section !== 'body') return;

                // Spalte Datum: das kleine „online" unter dem Zeitstempel.
                if (data.column.index === 3) {
                    if (!online[data.row.index]) return;
                    doc.setFont('helvetica', 'italic');
                    doc.setFontSize(SCHRIFT.klein - 1);
                    doc.setTextColor(tp0, tp1, tp2);
                    doc.text('online', data.cell.x + 2, data.cell.y + data.cell.height - 2.5);
                    doc.setFont('helvetica', 'normal');
                    return;
                }

                if (data.column.index !== 4) return;
                const bild = bilder[data.row.index];
                if (!bild) return;
                try {
                    // Seitenverhaeltnis wahren und in die Zelle einpassen —
                    // ein verzerrter Namenszug ist keine Unterschrift.
                    const box = fitLogoBox(
                        doc.getImageProperties(bild),
                        data.cell.width - 4,
                        data.cell.height - 3,
                    );
                    if (!box) return;
                    doc.addImage(
                        bild,
                        imgFmt(bild),
                        data.cell.x + 2,
                        data.cell.y + (data.cell.height - box.height) / 2,
                        box.width,
                        box.height,
                    );
                } catch (e) {
                    console.warn('PDF: Unterschrift konnte nicht eingebettet werden', e);
                }
            },
        });

        y = ((doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY ?? y) + 14;

        // ── Feld des Durchfuehrenden ─────────────────────────────────────
        platzFuer(20);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(SCHRIFT.feld);
        doc.setTextColor(tp0, tp1, tp2);
        doc.text(`Durchführender: ${strich(einweisung.durchfuehrender_name)}`, margin, y);
        y += 14;
        doc.setDrawColor(ln0, ln1, ln2);
        doc.setLineWidth(0.2);
        doc.line(margin, y, margin + 75, y);
        doc.setFontSize(SCHRIFT.klein);
        doc.text('Durchführender / Unterschrift', margin, y + 4);
    }

    // Fusszeile und Seitenzahl zuletzt — vorher steht die Gesamtzahl nicht fest.
    drawFooters(doc, company, lh, geo);

    const seitenGesamt = doc.getNumberOfPages();
    return {
        seitenGesamt,
        seitenInhalt,
        passt: seitenInhalt <= MAX_SEITEN,
    };
};

/**
 * Erzeugt das Blatt und sagt zugleich, wie viele Seiten es geworden sind.
 *
 * Fuer die Oberflaeche gedacht, die beides braucht (Datei UND die verbindliche
 * Aussage „passt auf 2 Seiten") und das Dokument sonst zweimal setzen muesste.
 */
export const generateEinweisungPdfMitSeiten = (
    einweisung: SigeEinweisung,
    company: CompanyInfo,
    optionen?: EinweisungPdfOptionen,
): { doc: jsPDF; seiten: EinweisungSeiten } => {
    const doc = optionen?.__docFactory
        ? optionen.__docFactory()
        : new jsPDF({ format: 'a4', unit: 'mm', compress: true });
    const seiten = renderEinweisung(doc, einweisung, company, optionen);
    return { doc, seiten };
};

/** Das Blatt als jsPDF-Dokument. */
export const generateEinweisungPdf = (
    einweisung: SigeEinweisung,
    company: CompanyInfo,
    optionen?: EinweisungPdfOptionen,
): jsPDF => generateEinweisungPdfMitSeiten(einweisung, company, optionen).doc;

/**
 * Die VERBINDLICHE Seitenzahl — gesetzt mit den echten Schriftmetriken.
 *
 * `schaetzeSeiten` in services/sigeEinweisung.ts rechnet mit Zeichen je Zeile
 * und darf deshalb nie etwas sperren. Wer wissen muss, ob das Blatt wirklich
 * auf zwei Seiten passt, fragt hier.
 */
export const zaehleEinweisungSeiten = (
    einweisung: SigeEinweisung,
    company: CompanyInfo,
    optionen?: EinweisungPdfOptionen,
): EinweisungSeiten => generateEinweisungPdfMitSeiten(einweisung, company, optionen).seiten;
